import chroma from "chroma-js";
import flattenDeep from "lodash/flattenDeep";
import isArray from "lodash/isArray";
import isPlainObject from "lodash/isPlainObject";
import mapValues from "lodash/mapValues";
import memoize from "lodash/memoize";
import pickBy from "lodash/pickBy";

import makeChromaScale from "./makeChromaScale";

function pickNumeric(obj) {
  return pickBy(obj, (_, key) => !isNaN(key));
}

export default (theme) => {
  let {
    colors,
    minContrast = 4.5,
    colorResolvers = {},
    textColors = ["white", "black"],
    linkColors = ["white", "primary"],
    iconColor = textColors,
  } = theme;

  const defaultColorResolvers = {
    hover: "+50",
    active: "+100",
    text: (color) => {
      return color.findByContrast(textColors);
    },
    link: (color) => {
      return color.findByContrast(linkColors);
    },
    icon: (color) => {
      return color.findByContrast(iconColor);
    },
  };

  colorResolvers = {
    ...defaultColorResolvers,
    ...colorResolvers,
  };

  colorResolvers = mapValues(colorResolvers, (value) =>
    typeof value === "function" ? value : () => value,
  );

  class Color {
    constructor(path) {
      this._scale = null;
      this._shade = Number.NaN;
      this.resolve(path);
    }
    clone() {
      let clone = new Color();
      clone.scale = this.scale;
      clone._shade = this._shade;
      return clone;
    }
    resolve(path) {
      if (isPlainObject(path)) {
        this.scale = path;
        this.shade = Number.NaN;
      } else {
        path = isArray(path) ? flattenDeep(path).join(".") : String(path) || "";
        this.path = path;
        let parts = path.split(".").filter(Boolean);
        parts.forEach((part) => {
          this.resolvePart(part);
        });
      }
      return this;
    }
    resolvePart(part) {
      if (part === "transparent") {
        this.scale = "transparent";
        this.shade = Number.NaN;
      } else if (part === "currentColor" || part === "inherit") {
        this.scale = part;
        this.shade = Number.NaN;
      } else if (/^(\d+)$/.test(part)) {
        this.shade = Number(part);
      } else if (/^([+-]\d+)$/.test(part)) {
        if (Number.isNaN(this.shade)) {
          this.shade = this.finiteShade;
        }
        this.shade += Number(part.replace("+", ""));
      } else if (this.scale?.[part]) {
        let path = this.scale[part];
        let ref = this.clone().resolve(path);
        this.scale = ref.scale;
        this.shade = ref.shade;
      } else if (colorResolvers[part]) {
        let resolver = colorResolvers[part];
        let path = resolver(this);
        let ref = this.clone().resolve(path);
        this.scale = ref.scale;
        this.shade = ref.shade;
      } else if (colors[part]) {
        let ref = this.clone().resolve(colors[part]);
        this.scale = ref.scale;
        this.shade = ref.shade;
      } else if (chroma.valid(part)) {
        let color = chroma(part);
        let mainShade = 1000 - Math.round(color.get("oklch.l") * 1000);
        this.scale = { [mainShade]: color.toString(), main: mainShade };
        this.shade = Number.NaN;
      } else if (part === "main") {
        this.shade = this.scale?.main ?? Number.NaN;
      } else {
        this.scale = null;
        this.shade = Number.NaN;
      }
      return this;
    }
    get scale() {
      return this._scale;
    }
    set scale(value) {
      if (isPlainObject(value)) {
        this._scale = { ...value };
        if (this._scale.main == null) {
          this._scale.main = 500;
        } else if (isNaN(this._scale.main)) {
          let mainColor = getColor(this._scale.main);
          if (typeof mainColor.scale === "string") {
            this._scale = {};
          } else {
            this._scale = { ...this._scale, ...mainColor.scale };
          }
          this._scale[mainColor.finiteShade] = mainColor;
          this._scale.main = mainColor.finiteShade;
        }
      } else {
        this._scale = value;
      }
    }
    get mainShade() {
      return this.scale?.main ?? Number.NaN;
    }
    get shade() {
      return this._shade;
    }
    set shade(value) {
      if (value == null) {
        this._shade = Number.NaN;
      }
      this._shade = Number(value);
    }
    get finiteShade() {
      if (Number.isFinite(this.shade)) {
        return this.shade;
      }
      return this.mainShade;
    }
    get chromaColor() {
      return chroma(this.toString());
    }
    getContrast(otherColor) {
      let color = this.toString();
      let other = otherColor.toString();
      return chroma.contrast(color, other);
    }
    findByContrast(colors, contrast = minContrast) {
      try {
        let color = colors.find(
          (color) => this.getContrast(this.clone().resolve(color)) >= contrast,
        );
        return color || colors[0];
      } catch {
        return "inherit";
      }
    }
    get chromaScale() {
      if (isPlainObject(this.scale)) {
        let numericKeys = mapValues(pickNumeric(this.scale), String);
        let chromaScale = makeChromaScale(numericKeys);
        return chromaScale;
      }
      return () => this.scale;
    }
    toString() {
      try {
        if (typeof this.scale === "string") {
          return this.scale;
        }
        if (this.shade >= 1000) {
          return "#000000";
        }
        if (this.shade <= 0) {
          return "#ffffff";
        }
        if (isPlainObject(this.scale)) {
          let scale = this.chromaScale;
          return scale(this.finiteShade).hex();
        }
        throw new Error("Could not convert to string");
      } catch (e) {
        return `invalid-color(${this.path})`;
      }
    }
  }

  var getColor = memoize(
    (path) => {
      let color = new Color(path);
      return color;
    },
    (path) => (isArray(path) ? flattenDeep(path).join(".") : path),
  );
  return (...args) => getColor(...args).toString();
};
